import { Injectable, computed, signal } from '@angular/core';
import { LocalStorageManagerService, UserProfile } from '../storage/local-storage-manager.service';
import { TokenStoreService } from '../auth/token-store.service';
import { logAuthSession } from '../auth/auth-session-log';
import { clearPromptShown } from '../utils/permission-prompt-session';

export interface TokenPayload {
  accessToken: string;
  refreshToken: string;
  ssoToken?: string;
  expiresOn?: string;
  refreshExpiresOn?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isGuest: boolean;
  accessToken: string | null;
  guestToken: string | null;
  user: UserProfile | null;
  isLoading: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isGuest: false,
  accessToken: null,
  guestToken: null,
  user: null,
  isLoading: true,
};

/**
 * Signal-based replacement for Consumer's Redux `authSlice`. Same action
 * names as methods (setTokens/setGuestToken/setUser/mergeUser/logout/hydrate)
 * so the state-transition behavior stays identical; components read state via
 * the exposed signals instead of `useAppSelector`.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private state = signal<AuthState>(initialState);

  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly isGuest = computed(() => this.state().isGuest);
  readonly accessToken = computed(() => this.state().accessToken);
  readonly guestToken = computed(() => this.state().guestToken);
  readonly user = computed(() => this.state().user);
  readonly isLoading = computed(() => this.state().isLoading);

  /**
   * Incremented whenever the session identity changes (logout, login). TokenRefreshService
   * captures it before a refresh request and discards the response if it moved, so a refresh
   * that was in flight during logout can't write the old session's tokens back (CF-06).
   */
  get sessionEpoch(): number {
    return this.epoch;
  }
  private epoch = 0;

  /**
   * True once app.ts has started the bootstrap that ends in `hydrate()`. Until then a browser /
   * install gate may legitimately hold the app for as long as the user takes, so
   * `waitForAuthHydration`'s backstop must not run its clock yet.
   */
  readonly bootstrapStarted = signal(false);

  markBootstrapStarted(): void {
    this.bootstrapStarted.set(true);
  }

  constructor(
    private localStorageManager: LocalStorageManagerService,
    private tokenStore: TokenStoreService,
  ) {}

  setTokens(payload: TokenPayload): void {
    const { accessToken, refreshToken, ssoToken, expiresOn, refreshExpiresOn } = payload;
    this.epoch++;
    this.localStorageManager.setAccessToken(accessToken);
    if (refreshToken) this.localStorageManager.setRefreshToken(refreshToken);
    if (ssoToken) this.localStorageManager.setSsoToken(ssoToken);
    if (expiresOn) this.localStorageManager.setTokenExpiry(expiresOn);
    if (refreshExpiresOn) this.localStorageManager.setRefreshTokenExpiry(refreshExpiresOn);

    this.state.update((s) => ({ ...s, isAuthenticated: true, isGuest: false, accessToken }));
  }

  /**
   * A refresh rotated the JWT within the same session: keep the `accessToken` signal current
   * (CF-20 — it was only ever set at login/hydrate). Persistence is TokenRefreshService's job,
   * and the session epoch deliberately does not move.
   */
  updateAccessToken(accessToken: string): void {
    this.state.update((s) => ({ ...s, accessToken }));
  }

  setGuestToken(token: string): void {
    this.localStorageManager.setGuestToken(token);
    this.state.update((s) => ({ ...s, guestToken: token, isGuest: true }));
  }

  setUser(user: UserProfile): void {
    this.localStorageManager.setUserProfile(user);
    if (user.subscriberId) this.localStorageManager.setSubscriberId(user.subscriberId);
    if (user.mobile) this.localStorageManager.setMobileNumber(user.mobile);
    this.state.update((s) => ({ ...s, user }));
  }

  mergeUser(partial: Partial<UserProfile>): void {
    const merged = { ...(this.state().user ?? { userName: '' }), ...partial };
    this.localStorageManager.setUserProfile(merged);
    if (merged.subscriberId) this.localStorageManager.setSubscriberId(merged.subscriberId);
    if (merged.mobile) this.localStorageManager.setMobileNumber(merged.mobile);
    this.state.update((s) => ({ ...s, user: merged }));
  }

  logout(): void {
    logAuthSession('logout', 'warn');
    // Keep `isLoading` latched during bootstrap so guards waiting on
    // waitForAuthHydration do not wake mid force-logout before hydrate() runs.
    const keepLoading = this.state().isLoading;
    this.epoch++;
    this.tokenStore.clear();
    this.localStorageManager.clearAll();
    clearPromptShown('media');
    clearPromptShown('clipboard');
    this.state.set({ ...initialState, isLoading: keepLoading });
  }

  /** Populate state from whatever TokenStore/LocalStorageManager already hold (post-bootstrap). */
  hydrate(): void {
    const accessToken = this.tokenStore.getAccessToken();
    const guestToken = this.localStorageManager.getGuestToken();
    const user = this.localStorageManager.getUserProfile();

    this.state.set({
      isAuthenticated: !!accessToken,
      isGuest: !!guestToken && !accessToken,
      accessToken,
      guestToken,
      user,
      isLoading: false,
    });
  }
}
